// apps/web/src/components/threat-actors/ThreatActorDossierList.tsx
//
// KAN-221 — the ranked dossier list and its loading / error / empty states.
// Connects visually to the filter workbench above (shared border, rounded
// bottom). Rank is honest scan-order within the loaded+sorted view.

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertTriangle, SearchX } from 'lucide-react'
import type { ThreatActor } from '@/lib/types'
import { ThreatActorDossierRow } from './ThreatActorDossierRow'

const SHELL =
  'rounded-b-lg border border-t-0 border-border bg-card overflow-hidden'

function StatePanel({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode
  title: string
  body: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center px-7 py-16 text-center">
      <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl border border-border text-muted-foreground">
        {icon}
      </div>
      <p className="font-heading text-base font-semibold text-foreground">{title}</p>
      <p className="mx-auto mt-1.5 max-w-md text-[13px] text-muted-foreground">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

export interface ThreatActorDossierListProps {
  actors?: ThreatActor[]
  isLoading?: boolean
  isError?: boolean
  hasActiveFilters?: boolean
  onRetry?: () => void
  onReset?: () => void
  now?: Date
}

export function ThreatActorDossierList(props: ThreatActorDossierListProps) {
  if (props.isLoading) {
    return (
      <div
        className={SHELL}
        data-testid="actor-list-loading"
        role="status"
        aria-label="Loading threat actors"
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="border-b border-border/60 px-5 py-4 last:border-0">
            <div className="flex items-center gap-3.5">
              <Skeleton className="h-3.5 w-5" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="ml-auto h-5 w-24" />
            </div>
            <Skeleton className="mt-3 h-3 w-2/3" />
            <div className="mt-3 flex gap-3">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (props.isError) {
    return (
      <div className={SHELL}>
        <StatePanel
          icon={<AlertTriangle className="h-5 w-5 text-destructive" aria-hidden="true" />}
          title="Couldn't load the actor registry"
          body="The intelligence service didn't respond. Your last synced view may be stale — retry to refresh."
          action={
            props.onRetry ? (
              <Button type="button" onClick={props.onRetry}>
                Retry
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  const actors = props.actors ?? []
  if (actors.length === 0) {
    return (
      <div className={SHELL}>
        <StatePanel
          icon={<SearchX className="h-5 w-5" aria-hidden="true" />}
          title="No actors match this view"
          body={
            props.hasActiveFilters
              ? 'Your filter combination excluded every actor. Clear filters to return to the full registry view.'
              : 'No actors are currently available in this view.'
          }
          action={
            props.onReset ? (
              <Button type="button" variant="outline" onClick={props.onReset}>
                Clear all filters
              </Button>
            ) : undefined
          }
        />
      </div>
    )
  }

  return (
    <div className={SHELL}>
      {actors.map((actor, i) => (
        <ThreatActorDossierRow
          key={actor.id}
          actor={actor}
          rank={i + 1}
          now={props.now}
        />
      ))}
    </div>
  )
}
